import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Feather, Sparkles, BookOpen, Wand2, ShieldCheck, Loader2, Mail, Eye, EyeOff, CheckCircle2, ArrowLeft } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { authRegister, authLogin, authForgotPassword, authResendVerification, authGoogle } from "@/lib/api";
import { authBackgroundStyle } from "@/lib/authBackground";

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

function formatApiErrorDetail(detail) {
    if (detail == null) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}

function passwordStrength(pw) {
    if (!pw) return { score: 0, label: "", color: "" };
    let score = 0;
    if (pw.length >= 8) score++;
    if (pw.length >= 12) score++;
    if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) score++;
    if (/\d/.test(pw)) score++;
    if (/[^A-Za-z0-9]/.test(pw)) score++;
    const buckets = [
        { label: "Too weak", color: "bg-red-400" },
        { label: "Weak", color: "bg-orange-400" },
        { label: "Fair", color: "bg-yellow-400" },
        { label: "Good", color: "bg-lime-400" },
        { label: "Strong", color: "bg-emerald-400" },
    ];
    const idx = Math.min(score, 4);
    return { score: idx, ...buckets[idx] };
}

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function Login() {
    const [mode, setMode] = useState("signin"); // "signin" | "signup" | "forgot"
    const [showEmail, setShowEmail] = useState(false);
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [name, setName] = useState("");
    const [showPw, setShowPw] = useState(false);
    const [error, setError] = useState("");
    const [notice, setNotice] = useState("");
    const [needsVerify, setNeedsVerify] = useState(false);
    const [busy, setBusy] = useState(false);

    const strength = passwordStrength(password);

    function switchMode(next) {
        setMode(next);
        setError("");
        setNotice("");
        setNeedsVerify(false);
    }

    // Google sign-in via Google Identity Services. The GSI script (loaded in
    // index.html) renders its own button into `googleBtnRef` and hands us an
    // ID token, which the backend verifies at POST /api/auth/google.
    const googleBtnRef = useRef(null);
    const [googleReady, setGoogleReady] = useState(false);
    useEffect(() => {
        if (!GOOGLE_CLIENT_ID) return;
        let tries = 0;
        const timer = setInterval(() => {
            tries += 1;
            const g = window.google?.accounts?.id;
            if (!g) {
                if (tries > 100) clearInterval(timer); // ~10s: script blocked or offline
                return;
            }
            clearInterval(timer);
            g.initialize({
                client_id: GOOGLE_CLIENT_ID,
                callback: async (resp) => {
                    setBusy(true);
                    setError("");
                    try {
                        await authGoogle(resp.credential);
                        window.location.href = "/";
                    } catch (err) {
                        setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
                        setBusy(false);
                    }
                },
                ux_mode: "popup",
                auto_select: false,
            });
            if (googleBtnRef.current) {
                g.renderButton(googleBtnRef.current, {
                    theme: "outline", size: "large", shape: "pill", text: "continue_with", width: 320,
                });
            }
            setGoogleReady(true);
        }, 100);
        return () => clearInterval(timer);
    }, []);

    async function resendVerification() {
        setBusy(true);
        try {
            const res = await authResendVerification(email.trim(), window.location.origin);
            setNotice(res.message || "Verification email sent.");
            setNeedsVerify(false);
        } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        }
        setBusy(false);
    }

    async function submitEmail(e) {
        e.preventDefault();
        setError("");
        setNotice("");
        setNeedsVerify(false);
        setBusy(true);
        try {
            if (mode === "forgot") {
                const res = await authForgotPassword(email.trim(), window.location.origin);
                setNotice(res.message || "If an account exists for that email, a reset link has been sent.");
                setBusy(false);
                return;
            }
            if (mode === "signup") {
                const res = await authRegister(email.trim(), password, name.trim(), window.location.origin);
                setNotice(res.message || "Account created. Check your email to verify your address.");
                setBusy(false);
                return;
            }
            await authLogin(email.trim(), password);
            // Reboot the app so AuthProvider picks up the new session cookie.
            window.location.href = "/";
        } catch (err) {
            if (err.response?.status === 403) setNeedsVerify(true);
            setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
            setBusy(false);
        }
    }

    return (
        <div
            className="min-h-screen relative text-white"
            style={authBackgroundStyle}
        >
            {/* Layered overlays: vertical darken + left-side dramatic vignette for copy legibility */}
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/70" />
            <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/40 to-black/10" />

            <div className="relative z-10 min-h-screen flex flex-col p-8 sm:p-10 lg:p-16">
                <header className="flex items-center gap-2">
                    <Feather className="w-5 h-5 text-white/90" />
                    <span className="font-heading text-2xl tracking-tight text-white">Day Dreamer</span>
                    <span className="ml-2 text-[11px] uppercase tracking-[0.25em] text-white/60 hidden sm:inline">by H.Tokoki</span>
                </header>

                <main className="flex-1 flex items-center">
                    <div className="max-w-xl w-full">
                        <div className="text-xs uppercase tracking-[0.25em] text-white/70 mb-6">Novelist · Editor · Proofreader</div>
                        <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-light leading-[1.05] tracking-tight text-white">
                            Write the novel<br /><em className="text-white/95">only you</em> could write.
                        </h1>
                        <p className="mt-6 text-base lg:text-lg text-white/80 leading-relaxed">
                            A quiet desk for serious writers — outline, draft, edit, and proofread with an AI collaborator that reads your characters, your plot, and your voice.
                        </p>

                        <div className="mt-8 space-y-3 text-sm">
                            <Feature icon={Wand2}>AI co-writing with Claude Sonnet 5 or GPT-5.6 Sol</Feature>
                            <Feature icon={ShieldCheck}>Inline proofreading and track-changes</Feature>
                            <Feature icon={BookOpen}>Per-chapter version history & export to .docx / .epub / .pdf</Feature>
                            <Feature icon={Sparkles}>Focus mode, daily goals, writing streaks</Feature>
                        </div>

                        <div className="mt-10 max-w-sm">
                            {GOOGLE_CLIENT_ID ? (
                                <div className="flex justify-center min-h-[48px]" data-testid="google-login-button">
                                    <div ref={googleBtnRef} />
                                    {!googleReady && (
                                        <div className="inline-flex items-center gap-2 text-sm text-white/70">
                                            <GoogleIcon /> Loading Google sign-in…
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <Button size="lg" disabled className="w-full rounded-full px-7 h-12 text-base bg-white/60 text-foreground">
                                    <GoogleIcon /> Google sign-in not configured
                                </Button>
                            )}

                            {!showEmail ? (
                                <button
                                    data-testid="show-email-auth"
                                    onClick={() => { setShowEmail(true); switchMode("signin"); }}
                                    className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-full px-7 h-12 text-base border border-white/40 text-white hover:bg-white/10 transition-colors"
                                >
                                    <Mail className="w-4 h-4" /> Continue with email
                                </button>
                            ) : mode === "forgot" ? (
                                <form onSubmit={submitEmail} className="mt-4 space-y-3" data-testid="forgot-form">
                                    <button type="button" data-testid="forgot-back"
                                        onClick={() => switchMode("signin")}
                                        className="inline-flex items-center gap-1.5 text-sm text-white/80 hover:text-white transition-colors">
                                        <ArrowLeft className="w-4 h-4" /> Back to sign in
                                    </button>
                                    <p className="text-sm text-white/80">Enter your email and we'll send you a link to reset your password.</p>
                                    <Input data-testid="forgot-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                                        placeholder="you@example.com" autoComplete="email"
                                        className="h-11 bg-white/95 text-foreground placeholder:text-muted-foreground border-0" />
                                    {notice && <p data-testid="auth-notice" className="text-sm text-emerald-300 flex items-start gap-1.5"><CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />{notice}</p>}
                                    {error && <p data-testid="auth-error" className="text-sm text-red-300">{error}</p>}
                                    <Button data-testid="forgot-submit" type="submit" disabled={busy}
                                        className="w-full rounded-full h-12 text-base bg-white text-foreground hover:bg-white/90">
                                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                                        Send reset link
                                    </Button>
                                </form>
                            ) : (
                                <form onSubmit={submitEmail} className="mt-4 space-y-3" data-testid="email-auth-form">
                                    <div className="flex rounded-full bg-white/10 border border-white/20 p-1 text-sm">
                                        <button type="button" data-testid="mode-signin"
                                            onClick={() => switchMode("signin")}
                                            className={`flex-1 rounded-full py-1.5 transition-colors ${mode === "signin" ? "bg-white text-foreground" : "text-white/80"}`}>
                                            Sign in
                                        </button>
                                        <button type="button" data-testid="mode-signup"
                                            onClick={() => switchMode("signup")}
                                            className={`flex-1 rounded-full py-1.5 transition-colors ${mode === "signup" ? "bg-white text-foreground" : "text-white/80"}`}>
                                            Create account
                                        </button>
                                    </div>
                                    {mode === "signup" && (
                                        <Input data-testid="auth-name-input" value={name} onChange={(e) => setName(e.target.value)}
                                            placeholder="Your name (optional)" autoComplete="name"
                                            className="h-11 bg-white/95 text-foreground placeholder:text-muted-foreground border-0" />
                                    )}
                                    <Input data-testid="auth-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                                        placeholder="you@example.com" autoComplete="email"
                                        className="h-11 bg-white/95 text-foreground placeholder:text-muted-foreground border-0" />
                                    <div className="relative">
                                        <Input data-testid="auth-password-input" type={showPw ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)}
                                            placeholder={mode === "signup" ? "Create a password (min 8 characters)" : "Password"}
                                            autoComplete={mode === "signup" ? "new-password" : "current-password"}
                                            className="h-11 pr-11 bg-white/95 text-foreground placeholder:text-muted-foreground border-0" />
                                        <button type="button" data-testid="toggle-password"
                                            onClick={() => setShowPw((v) => !v)}
                                            aria-label={showPw ? "Hide password" : "Show password"}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
                                            {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                        </button>
                                    </div>
                                    {mode === "signup" && password && (
                                        <div data-testid="password-strength" className="space-y-1">
                                            <div className="flex gap-1">
                                                {[0, 1, 2, 3, 4].map((i) => (
                                                    <div key={i} className={`h-1 flex-1 rounded-full transition-colors ${i <= strength.score ? strength.color : "bg-white/20"}`} />
                                                ))}
                                            </div>
                                            <p className="text-xs text-white/70">Password strength: <span className="text-white/90">{strength.label}</span></p>
                                        </div>
                                    )}
                                    {mode === "signin" && (
                                        <div className="text-right">
                                            <button type="button" data-testid="forgot-link"
                                                onClick={() => switchMode("forgot")}
                                                className="text-xs text-white/80 hover:text-white underline underline-offset-2 transition-colors">
                                                Forgot password?
                                            </button>
                                        </div>
                                    )}
                                    {notice && <p data-testid="auth-notice" className="text-sm text-emerald-300 flex items-start gap-1.5"><CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />{notice}</p>}
                                    {error && <p data-testid="auth-error" className="text-sm text-red-300">{error}</p>}
                                    {needsVerify && (
                                        <button type="button" data-testid="resend-verification"
                                            onClick={resendVerification} disabled={busy}
                                            className="text-sm text-white/90 underline underline-offset-2 hover:text-white transition-colors">
                                            Resend verification email
                                        </button>
                                    )}
                                    <Button data-testid="auth-submit" type="submit" disabled={busy}
                                        className="w-full rounded-full h-12 text-base bg-white text-foreground hover:bg-white/90">
                                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                                        {mode === "signup" ? "Create account" : "Sign in"}
                                    </Button>
                                </form>
                            )}
                            <p className="mt-3 text-xs text-white/70">Your manuscripts are private to your account.</p>
                        </div>
                    </div>
                </main>

                {/* Signed-out visitors need to reach these: Google Play requires a
                    public privacy policy and account-deletion route, and the
                    FAQ answers pricing questions before anyone creates an account. */}
                <footer className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6 text-xs text-white/60">
                    <span className="uppercase tracking-[0.25em]">© Day Dreamer</span>
                    <nav className="flex flex-wrap gap-x-4 gap-y-1" data-testid="login-legal-links">
                        <Link to="/faq" className="hover:text-white">FAQ</Link>
                        <Link to="/privacy" className="hover:text-white">Privacy</Link>
                        <Link to="/terms" className="hover:text-white">Terms</Link>
                        <Link to="/delete-account" className="hover:text-white">Delete account</Link>
                        <a href="mailto:HELPDESK@htokoki.com" className="hover:text-white">Contact</a>
                    </nav>
                </footer>
            </div>
        </div>
    );
}

function Feature({ icon: Icon, children }) {
    return (
        <div className="flex items-start gap-2.5">
            <Icon className="w-4 h-4 text-white/95 mt-0.5" />
            <span className="text-white/90">{children}</span>
        </div>
    );
}

function GoogleIcon() {
    return (
        <svg width="16" height="16" viewBox="0 0 24 24" className="-ml-0.5">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09Z" />
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.25 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z" />
            <path fill="#FBBC05" d="M5.84 14.09a6.6 6.6 0 0 1 0-4.18V7.07H2.18a11 11 0 0 0 0 9.86l3.66-2.84Z" />
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.46 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38Z" />
        </svg>
    );
}
